import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BookOpen, ChevronDown } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { FACEBOOK_PERMISSIONS } from './facebookPermissions';

const STEP_KEYS = ['addSystemUser', 'assignAssets', 'generateToken', 'saveInQuantix', 'campaign'];

/**
 * Collapsible "how do I get a System User token" guide shown inside the
 * Integrations > Facebook Lead Ads module (contract: "Tenant setup", "Permissions").
 */
const FacebookSetupGuide: React.FC = () => {
    const { t } = useTranslation('crm');
    const [open, setOpen] = useState(false);

    return (
        <div className="rounded-lg border">
            <button
                type="button"
                onClick={() => setOpen(value => !value)}
                aria-expanded={open}
                className="flex w-full items-center justify-between gap-2 px-3 py-2 text-start text-sm font-medium"
            >
                <span className="flex items-center gap-2">
                    <BookOpen className="h-4 w-4" />
                    {t('campaigns.facebookSetup.title')}
                </span>
                <ChevronDown className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} />
            </button>

            {open && (
                <div className="space-y-4 border-t px-3 py-3 text-sm">
                    <p className="text-muted-foreground">{t('campaigns.facebookSetup.intro')}</p>

                    <ol className="list-decimal space-y-2 ps-5">
                        {STEP_KEYS.map(key => (
                            <li key={key}>{t(`campaigns.facebookSetup.steps.${key}`)}</li>
                        ))}
                    </ol>

                    <p className="text-xs text-muted-foreground">
                        {t('campaigns.facebookSetup.agencyNote')}
                    </p>

                    <div className="space-y-2">
                        <p className="font-medium">
                            {t('campaigns.facebookSetup.permissionsTitle')}
                        </p>
                        <div className="overflow-x-auto">
                            <table className="w-full text-start text-xs">
                                <thead>
                                    <tr className="border-b text-muted-foreground">
                                        <th className="py-1 pe-3 text-start font-medium">
                                            {t('campaigns.facebookSetup.columns.permission')}
                                        </th>
                                        <th className="py-1 pe-3 text-start font-medium">
                                            {t('campaigns.facebookSetup.columns.level')}
                                        </th>
                                        <th className="py-1 text-start font-medium">
                                            {t('campaigns.facebookSetup.columns.why')}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {FACEBOOK_PERMISSIONS.map(permission => (
                                        <tr
                                            key={permission.name}
                                            className="border-b last:border-0"
                                        >
                                            <td className="py-1.5 pe-3 align-top">
                                                <code className="rounded bg-muted px-1 py-0.5">
                                                    {permission.name}
                                                </code>
                                            </td>
                                            <td className="py-1.5 pe-3 align-top">
                                                <Badge
                                                    variant={
                                                        permission.level === 'required'
                                                            ? 'default'
                                                            : 'outline'
                                                    }
                                                >
                                                    {t(
                                                        `campaigns.facebookSetup.levels.${permission.level}`
                                                    )}
                                                </Badge>
                                            </td>
                                            <td className="py-1.5 align-top text-muted-foreground">
                                                {t(permission.descriptionKey)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <p className="text-xs text-muted-foreground">
                            {t('campaigns.facebookSetup.accessNote')}
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FacebookSetupGuide;
